import { createSlice, current } from "@reduxjs/toolkit";
const camelCase = require("camelcase");
import Keys from "Keys";
import { normalizeRoleDoc, rank } from "utils/permissionDependencies";
import { expandWildcardModules } from "utils/wildcardExpansion";

// B3: the "*" module expansion lives in utils/wildcardExpansion.js (derived from the
// taxonomy catalogue; unit-tested). See the TRAP note there before touching it.

// Permission-only module groups: they carry Base Permissions, never app tiles.
// NavbarLinks filters the apps grid through this (metaData.apps itself stays
// complete — it doubles as the caller-holdings fallback in the role editor).
export const PERMISSION_ONLY_MODULES = [
  "Solution Features",
  "Solution Edit",
];

const initialState = {
  services: [],
  userInfo: {},
  userCreds: {},
  selectedGroup: null,
  skip: true,
  loader: true,
  groupPermissions: {},
  // P2b: per-part Solution-Management permissions (Key Info/Asset/Monitoring/...) as a permission
  // map (NOT navigable routes). Kept out of appPaths; consumed by the solution-editor gating (P2c).
  partPermissions: {},
  // item-6: permission-only sub-tabs, keyed by module then tab (e.g.
  // modulePermissions["Alarms Management"]["Scheduled Reports"]). Only modules in
  // MODULE_PAGE_TABS contribute here; these tabs are NOT navigable routes (kept out
  // of appPaths). Consumers read with two-level optional chaining.
  modulePermissions: {},
  settings: false,
  // Tenant's enabled analytics granularity modes (dynamic bucket modes), populated
  // at app load from GET /aggregation/modes. Drives rolling-rule + chart selectors.
  analyticsModes: [],
  // Fleet Reports capability config, populated at app load from
  // GET /aggregation/fleet-config. TRI-STATE: null = not loaded yet (the nav
  // tile stays hidden and the page shows its loader), {enabled:false} = feature
  // off for this tenant, {enabled:true,...} = feature on. See useFleetKpiConfig.
  fleetKpi: null,
  multiAssetSensors:[],
  multiAssetActuators:[],
  branding: {
    primary: null,
    secondary: null,
    logo: null,
    name: null,
    userFlag: null,
    applied: false,
    id: null,
    brandingBlockList: [],
    pinnedSolutions: [],
  },
};

var pinnedSolutions;

function addCustomFont(font) {
  var customFont = new FontFace("customfont", "url('" + font + "')");
  customFont.load().then(function () {
    document.fonts.add(customFont);
    document.getElementsByTagName("body")[0].style.fontFamily = "customfont";
  });
}

function sortArray(data, template) {
  let arr = JSON.parse(JSON.stringify(data));
  arr.forEach((tab) => {
    let foundIndex = template.findIndex((t) => t == tab.name);
    if (foundIndex != -1) {
      tab.position = parseInt(foundIndex);
    }
  });
  arr.sort(function (a, b) {
    return a["position"] - b["position"];
  });
  return arr;
}

export const metaDataSlice = createSlice({
  name: "metaData",
  initialState,
  reducers: {
    setRole: (state, action) => {
      let metaData = {};
      // Taxonomy normalizer (load-time): map legacy module/tab/asset names onto
      // the target taxonomy and normalize permission values (rank-0 / DISABLE
      // sentinels dropped) BEFORE every derivation below — so appPaths,
      // modulePermissions, partPermissions and the services tab lists only
      // ever see the new names, regardless of migration state.
      const normalizedRole = normalizeRoleDoc(action.payload || {});
      const roleModules = normalizedRole?.modules || [];
      var services = JSON.parse(JSON.stringify(normalizedRole?.services || []));
      // Guard (2026-09-07): setBranding may legitimately run without pinnedSolutions (any
      // user without a personal settings doc), and this module-level var starts undefined.
      (pinnedSolutions || []).forEach((pinned) => {
        if (services.find((s) => s.serviceId == pinned.solution)) {
          services.find((s) => s.serviceId == pinned.solution).pinned =
            pinned.pin;
        }
      });
      let groupPermissions = {};
      let defaultApps = [{ name: "Solutions" }, { name: "Support" }];
      // B3: a "*" grant-all module confers every real module/tab, but the "*" sentinel itself
      // is not navigable. Expand it into the concrete Base-Permission catalogue at the "*" rank
      // so a "*"-holding role gets full navigation + modulePermissions/partPermissions (a
      // "*"-only role would otherwise have NO Administration menu and break-glass could never
      // reach Role Management). The literal "*" is dropped by expandWildcardModules, so it never
      // renders as a dead, iconless tile. Non-"*" roles are returned unchanged.
      const navModules = expandWildcardModules(roleModules);
      let apps = [...defaultApps, ...navModules];
      apps = sortArray(apps, [
        "Solutions",
        "Analytics",
        "Fleet Reports",
        "Solution Management",
        "Administration",
        "Support",
      ]);
      let arr = [];
      let appPaths = [];
      // P2b: the Solution-Management field-part tabs (Key Info/Asset/Monitoring/Value Insights/
      // Controlling/Configurations/Layout + granular parts) are PERMISSION FLAGS, not navigable
      // routes — adminRoutes has no component for them, so pushing them into appPaths crashes the
      // layout (adminRoutes[name] undefined). Only the 3 real page-tabs drive nav; the parts are
      // collected into a separate partPermissions map for the solution-editor gating.
      // The 3 real navigable Solution-Management routes (keep in sync with the adminRoutes keys in
      // layouts/solutionCreator.js). Any Solution-Management tab NOT in this set is a field-part
      // permission (-> partPermissions), never a route.
      const SOLUTION_PAGE_TABS = [
        "Solution Creation",
        "Solution Edit",
        "Threshold Management",
      ];
      // item-6: OPT-IN registry of modules whose tab list mixes real navigable
      // page-tabs with permission-only sub-tabs. Only modules listed here get the
      // split treatment; every other module keeps its every-tab-is-a-route behavior
      // verbatim. A tab NOT in a module's page-tab list is a permission flag
      // (-> modulePermissions), never a route (never pushed to appPaths) — so e.g.
      // "Scheduled Reports" under Alarms Management can't become a phantom route with
      // no component in adminRoutes.
      const MODULE_PAGE_TABS = {
        "Solution Management": SOLUTION_PAGE_TABS,
        // Administration mixes routes with the permission-only "Solution
        // Management" MASTER tab (R1) — the master must never become a phantom
        // route (adminRoutes has no component for it).
        Administration: [
          "User Management",
          "Role Management",
          "Device Management",
          "Group Management",
          "Brand Management",
        ],
        // Permission-only module groups: every tab is a permission flag, never
        // a route — empty page-tab lists keep them out of appPaths entirely
        // (a phantom route would crash the layout).
        "Solution Features": [],
        "Solution Edit": [],
      };
      let partPermissions = {};
      let modulePermissions = {};
      navModules.forEach((elm) => {
        sortArray(elm.tabs, [
          "User Management",
          "Role Management",
          "Device Management",
          "Group Management",
        ]).forEach((tab) => {
          const pageTabs = MODULE_PAGE_TABS[elm.name];
          const isPermissionOnly = pageTabs && !pageTabs.includes(tab.name);
          if (isPermissionOnly) {
            // permission-only sub-tab: record under modulePermissions[module][tab].
            if (!modulePermissions[elm.name]) modulePermissions[elm.name] = {};
            modulePermissions[elm.name][tab.name] = tab.permission;
          }
          if (elm.name == "Solution Management") {
            if (!SOLUTION_PAGE_TABS.includes(tab.name)) {
              // pure field-part (Key Info/Asset/Monitoring/...): a permission flag, NOT a route.
              partPermissions[tab.name] = tab.permission;
              return;
            }
            // Threshold Management is a page-tab AND a field-part (owns dataPointThresholds):
            // record it in partPermissions too, then fall through to also register its route.
            if (tab.name == "Threshold Management") {
              partPermissions[tab.name] = tab.permission;
            }
          }
          // Permission-only sub-tabs (e.g. "Scheduled Reports") have no component in
          // adminRoutes; a phantom route crashes the layout — so keep them out of appPaths.
          if (isPermissionOnly) return;
          // if (!(elm.name == "Administration" && tab.permission != "ALL"))
          appPaths.push({
            path: camelCase(tab.name).replace(" ", ""),
            name: tab.name,
            layout: `/${camelCase(elm.name).replace(" ", "")}/`,
            permission: tab.permission,
          });
        });
      });
      let idMap = {};
      // console.log({services})
      services.forEach((elm) => {
        // console.log({elm})
        groupPermissions[elm.serviceId] = elm.tabs.find(
          (tab) => tab.name == "Group Management"
        )
          ? elm.tabs.find((tab) => tab.name == "Group Management").permission
          : "DISABLE";
        if (elm.details) {
          // Renderable device-view assets ONLY: permission-only tabs (Edit
          // parts, Scheduled Reports, Threshold Profiles, Group Management)
          // must never become blank index-shifting device-view tabs.
          const RENDERABLE_DEVICE_ASSETS = [
            "Digital Twin",
            "Video Analytics",
            "Monitoring",
            "Analytics",
            "Tracking",
            "Controlling",
            "Configuration",
            "Rules",
            "Alarms",
            "Events",
            "History",
            "Metadata",
          ];
          let newTabs = sortArray(
            elm.tabs.filter((t) => t && RENDERABLE_DEVICE_ASSETS.includes(t.name)),
            [
              "Digital Twin",
              "Video Analytics",
              "Monitoring",
              "Analytics",
              "Tracking",
              "Controlling",
              "Configuration",
              "Rules",
              "Alarms",
              "Events",
              "History",
            ]
          );
          let service = elm.details;
          idMap[service.name] = service._id;
          // Solution sharing plan, stage 6: a solution with no configured asset (an unfinished one)
          // must not crash setRole, which would blank the whole app for every user who holds it
          // (incident 2026-10-05). It gets an empty asset list instead.
          const configured = Array.isArray(service.configuredAssets) ? service.configuredAssets : [];
          let assets = configured.length ? configured.map((asset) => {
            return {
              id: asset._id,
              name: asset.name,
              image: asset.logoPath
            }
          }) : service.configuredAsset ? [{
            name: service.configuredAsset.name,
            image: service.configuredAsset.logoPath,
          }] : [];
          let tabs = {};
          let tags = [];
          let defaultLocation = elm.details.defaultLocation;
          let widgetDatapoints = elm.details.widgetDatapoints;
          let trend = elm.details.trend;
          if (elm.details?.tags) tags = elm.details.tags.map((tag) => tag._id);
          let meta = [];
          let metaTags = [];
          if (elm.details?.metaTags) {
            meta = elm.details.metaTags.map((tag) => tag._id);
            metaTags = elm.details?.metaTags;
          }

          if (typeof service?.metaData === "string")
            tabs = JSON.parse(service?.metaData)?.tabs;
          else tabs = service?.metaData?.tabs;
          arr.push({
            name: service.name,
            id: service._id,
            description: service.description,
            sensors: service.configuredSensors.filter((e) => e.config != true),
            actuators: service.configuredActuators.filter(
              (e) => e.config != true
            ),
            configSensors: service.configuredSensors.filter(
              (e) => e.config == true
            ),
            configActuators: service.configuredActuators.filter(
              (e) => e.config == true
            ),
            dataPointThresholds: service?.dataPointThresholds
              ? service.dataPointThresholds
              : [],
            featureTabs: tabs,
            esbMetaData: service.esbAssetMapping,
            path: service._id,
            solutionLayout: service?.layout
              ? service?.layout
              : {
                  map: {
                    default: "Map",
                    mapModes: ["street", "light", "heat", "satellite"],
                    columns: ["deviceInfo", "datapoints", "metaTags"],
                  },
                },
            layout: "/solutions/",
            tabs: newTabs,
            group: elm.group,
            meta,
            cost: service.cost,
            unit: service.unit,
            target: service.target,
            metaTags,
            tags: tags,
            widgetDatapoints,
            trend,
            defaultLocation,
            assets: assets,
            image: elm.details.logoPath,
            profile: elm.details.profile,
            assetMapping: service.assetMapping,
            parentChildEnabled: service.parentChildEnabled,
            groupMetaData: elm.details.groupMetaData || [],
            currency: service.currency,
            multiplier: service.multiplier,
            energySavingEnable: service.energySavingEnable,
            temperatureUnit: service.temperatureUnit,
          });
          if (elm.pinned) {
            arr[arr.length - 1].pinned = elm.pinned;
          }
        }
      });
      metaData.apps = apps;
      metaData.idMap = idMap;
      metaData.servicesNew = services
        .filter((e) => e?.details)
        .map((e) => e?.details);
      metaData.services = arr;
      metaData.appPaths = appPaths;
      metaData.partPermissions = partPermissions;
      metaData.modulePermissions = modulePermissions;
      metaData.groupPermissions = groupPermissions;
      // Scheduled-Reports eligibility per solution (tolerant-absent, mirrors
      // the BE): while the role carries NO "Scheduled Reports" column anywhere
      // (pre-migration data), every held solution is eligible; once any column
      // exists (armed), only rows holding it at Partial+ are. ⊤ callers bypass
      // in the consumers. srEligible[sid] === false marks an ineligible row.
      const srArmed = services.some((elm) =>
        (elm.tabs || []).some((t) => t && t.name === "Scheduled Reports")
      );
      const srEligible = {};
      services.forEach((elm) => {
        const has = (elm.tabs || []).some(
          (t) => t && t.name === "Scheduled Reports"
        );
        srEligible[String(elm.serviceId)] = srArmed ? has : true;
      });
      metaData.srEligible = srEligible;
      // Threshold-Profiles access per solution (tolerant-absent, mirrors the
      // BE): while the role carries NO "Threshold Profiles" column anywhere,
      // every held solution is fully accessible; once any column exists
      // (armed), the column's rank binds (Partial = view, Full = manage).
      // tpAccess[sid] = "ALL" | "READ" | undefined (no access).
      const tpArmed = services.some((elm) =>
        (elm.tabs || []).some((t) => t && t.name === "Threshold Profiles")
      );
      const tpAccess = {};
      services.forEach((elm) => {
        const col = (elm.tabs || []).find(
          (t) => t && t.name === "Threshold Profiles"
        );
        tpAccess[String(elm.serviceId)] = col
          ? col.permission
          : tpArmed
            ? undefined
            : "ALL";
      });
      metaData.tpAccess = tpAccess;
      // Super-admin removal (Stage 3/4): the FE's "is-super-admin" is now DERIVED from
      // the wildcard grant — a role holding a "*" module AND a "*" service row grants
      // all — OR the legacy unscopedAdmin flag while it still exists (transition). So
      // every existing metaData.unscopedAdmin reader keeps working for a "*" role with
      // no per-site change, and survives Stage 4's flag drop. protectedAnchor is the
      // separate lockout marker the self-protection surfaces key on.
      // B4: the FE "is-super-admin" bypass (metaData.unscopedAdmin) gates high-power surfaces
      // (partAccess, Fleet Reports, bulk alarm actions), some with NO backend RBAC — so it must
      // require grant-all at FULL rank on BOTH axes, not mere row PRESENCE. A READ-'*' or
      // single-axis-'*' caller is scoped (the role editor reads its actual '*' rank via
      // callerWild*Rank), never the full bypass. wildRankOn returns the '*'-tab rank of a '*' row.
      const wildRankOn = (rows, key) => {
        const w = (rows || []).find((x) => x && String(x[key]) === "*");
        return w ? Math.max(0, ...(w.tabs || []).map((t) => (t && t.name === "*" ? rank(t.permission) : 0))) : 0;
      };
      const holdsWild =
        wildRankOn(normalizedRole?.modules, "name") >= 2 &&
        wildRankOn(normalizedRole?.services, "serviceId") >= 2;
      metaData.unscopedAdmin = !!(normalizedRole?.unscopedAdmin || holdsWild);
      metaData.protectedAnchor = !!normalizedRole?.protectedAnchor;
      // Solution sharing (plan stage 7): the own-role read says whether sharing is on for the tenant
      // (an older rolesMS sends nothing = off). The creation wizard and the solution menu read it.
      metaData.solutionSharing = normalizedRole?.solutionSharing === true;
      state = { ...state, ...metaData };
      return state;
    },
    setBranding: (state, action) => {
      let primaryColor = action.payload?.branding
        ? action.payload.branding.primaryColor
        : Keys?.primary
        ? Keys.primary
        : "#3399ff";
      let secondaryColor = action.payload?.branding
        ? action.payload.branding.secondaryColor
        : Keys?.secondary
        ? Keys.secondary
        : "#607d8b";
      let logo = action.payload?.branding
        ? action.payload.branding.logoPath
        : Keys?.logo
        ? Keys.logo
        : null;
      let name = action.payload?.branding
        ? action.payload.branding.name
        : Keys?.company
        ? Keys.company
        : null;
      let userFlag = action.payload?.userFlag;
      let brandingBlockList = action.payload?.brandingBlockList;
      let fonts;
      fonts = action.payload?.branding ? action.payload.branding.font : null;
      // Keep the previous pins when a branding payload carries none (the Branding popup and
      // menu dispatch without the field); never leave the var undefined (first-login crash).
      pinnedSolutions = action.payload?.pinnedSolutions
        ? action.payload?.pinnedSolutions
        : (pinnedSolutions || []);

      if (fonts) {
        if (!fonts.uploaded) {
          document.getElementsByTagName("body")[0].style.fontFamily =
            fonts.font;
        } else addCustomFont(fonts.font);
      } else if (Keys?.font) {
        if (Keys.uploaded != "true") {
          document.getElementsByTagName("body")[0].style.fontFamily = Keys.font;
        } else addCustomFont(Keys.font);
      } else {
        document.getElementsByTagName("body")[0].style.fontFamily = "Open Sans";
      }

      let res = {
        ...state,
        branding: {
          ...state.branding,
          id: action.payload?.branding ? action.payload?.branding._id : null,
          applied: action.payload?.branding ? true : false,
          primaryColor,
          secondaryColor,
          logo,
          name,
          fonts,
          pinnedSolutions,
        },
      };
      if (userFlag || userFlag == false) res.branding.userFlag = userFlag;
      if (brandingBlockList) res.branding.brandingBlockList = brandingBlockList;
      return res;
    },

    setBrandingBlockList: (state, action) => {
      state.branding.brandingBlockList = action.payload;
      return state;
    },

    setSelectedGroup: (state, action) => {
      state = { ...state, selectedGroup: action.payload };
      return state;
    },
    setUser: (state, action) => {
      state = { ...state, userInfo: action.payload };
      return state;
    },
    setUserCreds: (state, action) => {
      state = { ...state, userCreds: action.payload };
      return state;
    },
    setSettings: (state, action) => {
      state = { ...state, settings: action.payload ? true : false };
      return state;
    },
    setSkip: (state, action) => {
      state = { ...state, skip: action.payload };
      return state;
    },
    setServices: (state, action) => {
      state = { ...state, services: action.payload };
      return state;
    },
    setLoader: (state, action) => {
      state = { ...state, loader: action.payload };
      return state;
    },
    setMultiAssetSensors: (state, action) => {
      state = { ...state, multiAssetSensors: action.payload };
      return state;
    },
    setAnalyticsModes: (state, action) => {
      state = { ...state, analyticsModes: Array.isArray(action.payload) ? action.payload : [] };
      return state;
    },
    setFleetKpiConfig: (state, action) => {
      state = {
        ...state,
        fleetKpi:
          action.payload && typeof action.payload === "object"
            ? action.payload
            : { enabled: false },
      };
      return state;
    },
    resetMetaData: (state) => {
      // The module-level pin cache is session state: clear it here so a same-tab logout/login
      // of another user does not inherit the previous user's pins (setBranding keeps the
      // previous pins when a payload carries none, which is only right within one session).
      pinnedSolutions = undefined;
      state = initialState;
      return state;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setRole,
  setBranding,
  setSelectedGroup,
  setUser,
  setServices,
  setSkip,
  setLoader,
  resetMetaData,
  setSettings,
  setBrandingBlockList,
  setUserCreds,
  setMultiAssetSensors,
  setAnalyticsModes,
  setFleetKpiConfig
} = metaDataSlice.actions;

export default metaDataSlice.reducer;
